import { Button, Grid, Paper, ScrollArea, Text } from "@mantine/core";
import {
    useBrowserContext,
    useCreatePriorityNoteMutation,
    useCreateUnifiedPriorityEntryMutation
} from "@therapprove/common";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    createUnifiedPriorityEntryRow,
    parseCSV,
    rowToWaitlistEntry
} from "./utils/import";

/**
 Run this migration in PROD to change the created_by_id to Therapprove.

 UPDATE priority_notes
 SET created_by_id = 'cb05e453-f52a-4743-a257-42d6650668fc'
 WHERE organization_id = '<ORG ID HERE>'
 AND created_by_id = 'f89a24c2-eeb1-48d1-b28f-ea7295df7c43'
 */

export const CustomScripts = () => {
    // Refs
    const scrollRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    //  State
    const [logs, setLogs] = useState<string[]>([]);
    const [importingWaitlist, setImportingWaitlist] = useState(false);

    // Hooks
    const { isLocalhost } = useBrowserContext();
    const navigate = useNavigate();

    // Mutations
    const [, createUnifiedPriorityEntry] =
        useCreateUnifiedPriorityEntryMutation();
    const [, createPriorityNote] = useCreatePriorityNoteMutation();

    // Scroll to bottom when logs change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [logs]);

    const importWaitlist = async (csvContent: string) => {
        try {
            // Parse the CSV
            const rows = parseCSV(csvContent);

            // Skip the header row and process each data row
            const dataRows = rows.slice(1);

            setLogs((prevLogs) => [
                ...prevLogs,
                `[${new Date().toLocaleTimeString()}] Found ${
                    dataRows.length
                } waitlist entries`
            ]);

            // Loop through each row and log it
            for (let index = 0; index < dataRows.length; index++) {
                const row = dataRows[index];
                setLogs((prevLogs) => [
                    ...prevLogs,
                    `[${new Date().toLocaleTimeString()}] "=".repeat(80)`
                ]);
                setLogs((prevLogs) => [
                    ...prevLogs,
                    `[${new Date().toLocaleTimeString()}] Row ${index + 1}`
                ]);

                // Also log as structured object
                const waitlistRow = rowToWaitlistEntry(row);
                setLogs((prevLogs) => [
                    ...prevLogs,
                    `[${new Date().toLocaleTimeString()}] ${waitlistRow.caregivers} -> ${
                        waitlistRow.patientName
                    }`
                ]);
                const { entry, notes } =
                    createUnifiedPriorityEntryRow(waitlistRow);

                // Create the entry
                const createEntryResponse = await createUnifiedPriorityEntry({
                    input: entry
                });

                const priorityEntry =
                    createEntryResponse.data?.priorityEntries
                        ?.createUnifiedPriorityEntry;

                // Add notes to the entry
                for (const note of notes) {
                    await createPriorityNote({
                        input: {
                            note,
                            priorityEntryId:
                                priorityEntry?.at(0)?.priorityEntryId || "",
                            caregiverId:
                                priorityEntry?.at(0)?.caregiver?.userId ||
                                "",
                            patientId:
                                priorityEntry?.at(0)?.patient?.patientId || ""
                        }
                    });
                }
            }
        } catch (error) {
            console.error("Error processing CSV:", error);
            setLogs((prev) => [
                ...prev,
                `[${new Date().toLocaleTimeString()}] Error: ${error}`
            ]);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            setImportingWaitlist(false);
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const csvContent = e.target?.result as string;
            if (csvContent) {
                await importWaitlist(csvContent);
                // Log the results
                setLogs((prev) => [
                    ...prev,
                    `[${new Date().toLocaleTimeString()}] Done!`
                ]);
            }
            setImportingWaitlist(false);
        };
        reader.onerror = () => {
            setLogs((prev) => [...prev, `Error reading file`]);
            setImportingWaitlist(false);
        };
        reader.readAsText(file);
    };

    /******************************************************************
     * NOTE: This is only available on localhost, for local development
     ******************************************************************/
    if (!isLocalhost) {
        navigate("/");
    }

    return (
        <div>
            <div>
                <Grid>
                    <Grid.Col span={4}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            onChange={handleFileSelect}
                        />
                        <Button
                            loading={importingWaitlist}
                            onClick={async () => {
                                setImportingWaitlist(true);
                                setLogs((prev) => [
                                    ...prev,
                                    `[${new Date().toLocaleTimeString()}] Starting...`
                                ]);

                                // Trigger file selection
                                fileInputRef.current?.click();
                            }}
                        >
                            Import CSV To Pipeline
                        </Button>
                    </Grid.Col>
                </Grid>
                <Paper
                    mt="md"
                    p="md"
                    style={{
                        backgroundColor: "var(--mantine-color-dark-7)",
                        color: "var(--mantine-color-green-5)",
                        fontFamily: "monospace",
                        height: "400px"
                    }}
                >
                    <ScrollArea h="100%" viewportRef={scrollRef}>
                        {logs.map((log, index) => (
                            <Text
                                variant="body"
                                key={index}
                                size="sm"
                                style={{ marginBottom: "4px" }}
                            >
                                {log}
                            </Text>
                        ))}
                    </ScrollArea>
                </Paper>
            </div>
        </div>
    );
};
